
import { HabitType } from '@/types/habit';

export const HABITICA_VALUE_CONFIG = {
  MIN_VALUE: -20.0,
  MAX_VALUE: 20.0,
  DEFAULT_VALUE: 0.0,
  // Precise Habitica constants
  TASK_VALUE_UP: 0.9747,
  TASK_VALUE_DIVISOR: 21.2748,
  HP_LOSS_MULTIPLIER: 0.09747,
  // Base XP values by difficulty and type
  BASE_XP: {
    daily: { trivial: 6, easy: 7, medium: 8, hard: 10 },
    habit: { trivial: 6, easy: 6, medium: 7, hard: 8 },
    todo: { trivial: 10, easy: 12, medium: 13, hard: 15 }
  },
  BASE_GOLD: {
    daily: { trivial: 3, easy: 4, medium: 5, hard: 6 },
    habit: { trivial: 3, easy: 3, medium: 4, hard: 5 },
    todo: { trivial: 6, easy: 7, medium: 8, hard: 9 }
  }
};

export function calculateTaskValue(habit: HabitType): number {
  return Math.max(HABITICA_VALUE_CONFIG.MIN_VALUE, 
    Math.min(HABITICA_VALUE_CONFIG.MAX_VALUE, habit.habitValue));
}

// Precise Habitica task.value change formulas
export function calculatePositiveTaskValueChange(currentValue: number): number {
  return HABITICA_VALUE_CONFIG.TASK_VALUE_UP * (1 - currentValue / HABITICA_VALUE_CONFIG.TASK_VALUE_DIVISOR);
}

export function calculateNegativeTaskValueChange(currentValue: number): number {
  return -HABITICA_VALUE_CONFIG.TASK_VALUE_UP * (1 + currentValue / HABITICA_VALUE_CONFIG.TASK_VALUE_DIVISOR);
}

// Precise Habitica HP loss calculation
export function calculateHPLoss(taskValue: number, constitution: number = 1.0): number {
  return HABITICA_VALUE_CONFIG.HP_LOSS_MULTIPLIER * (1 + taskValue / HABITICA_VALUE_CONFIG.TASK_VALUE_DIVISOR) * constitution;
}

export function calculateHabiticaReward(habit: HabitType): { experience: number; gold: number; newTaskValue: number; hpLoss?: number } {
  // Defensive checks for required properties
  if (!habit) {
    console.error('calculateHabiticaReward called with undefined habit');
    return { experience: 0, gold: 0, newTaskValue: 0 };
  }

  const currentTaskValue = calculateTaskValue(habit);
  
  let baseXP: number;
  let baseGold: number;
  let newTaskValue = currentTaskValue;
  let hpLoss: number | undefined;
  
  // Ensure we have valid defaults for all required properties
  const habitType = habit.type || 'habit';
  const difficulty = habit.difficulty || 'medium';
  
  if (habitType === 'todo') {
    // Age-based reward for todos using sqrt formula
    const ageInDays = habit.createdAt ? 
      Math.floor((Date.now() - new Date(habit.createdAt).getTime()) / (1000 * 60 * 60 * 24)) : 0;
    
    // Ensure the type exists in our config, fallback to habit if not
    const typeConfig = HABITICA_VALUE_CONFIG.BASE_XP.todo || HABITICA_VALUE_CONFIG.BASE_XP.habit;
    const goldConfig = HABITICA_VALUE_CONFIG.BASE_GOLD.todo || HABITICA_VALUE_CONFIG.BASE_GOLD.habit;
    
    baseXP = typeConfig[difficulty] || typeConfig.medium || 7;
    baseGold = goldConfig[difficulty] || goldConfig.medium || 4;
    
    const ageMultiplier = Math.sqrt(ageInDays + 1);
    const experience = Math.floor(baseXP * ageMultiplier);
    const gold = Math.floor(baseGold * ageMultiplier);
    
    return { experience, gold, newTaskValue };
  }
  
  // Precise Habitica formula: Reward = BaseReward × (1 + task.value)
  // Ensure the habit type exists in our config, fallback to habit if not
  const typeConfig = HABITICA_VALUE_CONFIG.BASE_XP[habitType] || HABITICA_VALUE_CONFIG.BASE_XP.habit;
  const goldConfig = HABITICA_VALUE_CONFIG.BASE_GOLD[habitType] || HABITICA_VALUE_CONFIG.BASE_GOLD.habit;
  
  baseXP = typeConfig[difficulty] || typeConfig.medium || 7;
  baseGold = goldConfig[difficulty] || goldConfig.medium || 4;
  
  const rewardMultiplier = Math.max(0, 1 + currentTaskValue);
  const experience = Math.floor(baseXP * rewardMultiplier);
  const gold = Math.floor(baseGold * rewardMultiplier);
  
  // Apply precise task value changes
  if (habit.type === 'daily') {
    const valueChange = calculatePositiveTaskValueChange(currentTaskValue);
    newTaskValue = Math.min(HABITICA_VALUE_CONFIG.MAX_VALUE, currentTaskValue + valueChange);
  } else if (habit.type === 'habit') {
    const valueChange = calculatePositiveTaskValueChange(currentTaskValue);
    newTaskValue = Math.min(HABITICA_VALUE_CONFIG.MAX_VALUE, currentTaskValue + valueChange);
  }
  
  return { experience, gold, newTaskValue };
}

export function calculateDailyMissPenalty(habit: HabitType, constitution: number = 1.0): { newTaskValue: number; hpLoss: number } {
  if (habit.type !== 'daily') return { newTaskValue: habit.habitValue, hpLoss: 0 };
  
  const currentValue = calculateTaskValue(habit);
  const valueChange = calculateNegativeTaskValueChange(currentValue);
  const newTaskValue = Math.max(HABITICA_VALUE_CONFIG.MIN_VALUE, currentValue + valueChange);
  const hpLoss = calculateHPLoss(currentValue, constitution);
  
  return { newTaskValue, hpLoss };
}

export function calculateNegativeHabitPenalty(habit: HabitType, constitution: number = 1.0): { newTaskValue: number; hpLoss: number } {
  if (habit.type !== 'habit') return { newTaskValue: habit.habitValue, hpLoss: 0 };
  
  const currentValue = calculateTaskValue(habit);
  const valueChange = calculateNegativeTaskValueChange(currentValue);
  const newTaskValue = Math.max(HABITICA_VALUE_CONFIG.MIN_VALUE, currentValue + valueChange);
  const hpLoss = calculateHPLoss(currentValue, constitution);
  
  return { newTaskValue, hpLoss };
}

export function getHabiticaColorState(habit: HabitType): 'green' | 'yellow' | 'red' {
  const taskValue = calculateTaskValue(habit);
  
  // Red: Low/negative task.value (high reward potential)
  if (taskValue < -5) return 'red';
  
  // Green: High task.value (diminished rewards)
  if (taskValue > 5) return 'green';
  
  // Yellow: Neutral task.value (normal rewards)
  return 'yellow';
}

export function shouldResetDailyCompletions(habit: HabitType): boolean {
  const now = new Date();
  const lastCompletion = habit.lastCompletionDate ? new Date(habit.lastCompletionDate) : null;
  
  if (!lastCompletion) return false;
  
  const daysSinceLastCompletion = Math.floor((now.getTime() - lastCompletion.getTime()) / (1000 * 60 * 60 * 24));
  return daysSinceLastCompletion >= 1;
}

export function calculateDailyDamage(missedDailies: HabitType[], constitution: number = 1.0): number {
  return missedDailies.reduce((damage, daily) => {
    const { hpLoss } = calculateDailyMissPenalty(daily, constitution);
    return damage + hpLoss;
  }, 0);
}

// Constitution system
export function getConstitutionModifier(playerClass: string | null, buffs: any[] = []): number {
  let baseConstitution = 1.0;
  
  // Class modifiers
  switch (playerClass) {
    case 'warrior':
      baseConstitution = 1.2; // Warriors take less damage
      break;
    case 'mage':
      baseConstitution = 0.8; // Mages are more fragile
      break;
    case 'healer':
      baseConstitution = 1.1; // Healers have some resistance
      break;
    case 'rogue':
      baseConstitution = 1.0; // Rogues are baseline
      break;
    default:
      baseConstitution = 1.0;
  }
  
  // Apply buff modifiers (future expansion)
  // buffs.forEach(buff => baseConstitution *= buff.constitutionModifier || 1.0);
  
  return baseConstitution;
}
